import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { apiFetch } from '../../api/api';
import './DataForm.css';
import SelectableInput from './SelectableInput';
import LookupField from './LookupField';
import { getSelectableColumns, isSelectableColumnSync } from '../../utils/columnConfig';
import { isFieldSelectable, isFieldLookup, formatFieldValue } from '../../utils/formatters';
import { getGlobalUnitSettings, resolveFieldUnitSync } from '../../utils/globalUnits';
import { FileDropZone } from '../file';
import { customCalculations } from '../../utils/customCalculations';
import DataExtraction from './DataExtraction';

export default function DataForm({ 
  // Single table mode props (legacy DataForm)
  data = {}, 
  tableName = '', 
  
  // Dual table mode props (DataForm2)
  tables = { parent: '', child: '' },
  identityColumns = { parent: '', child: '' },
  matchKey = { parentKey: '', childForeignKey: '' },
  recordId = null,
  defaultParentData = {},
  hiddenParentFields = [],
  
  // Common props
  onSave = null, 
  onCancel = null,
  title = 'Edit Data',
  skipAutoSave = false,
  columnsPerRow = 2,
  
  // New props for table-style editing
  viewMode = 'form', // 'form' or 'table'
  sectionConfig = null, // Section configuration for table-style editing
  tableData = [], // Array of records for table-style editing
  // Context props for custom calculations
  selectedWellId,
  reportDate,
  currentReportId
}) {
  // Determine mode based on props
  const isDualTableMode = !!(tables.parent && tables.child);
  const isSingleTableMode = !!tableName && !isDualTableMode;
  const isTableViewMode = viewMode === 'table' && sectionConfig;
  
  // Single table mode state
  const [formData, setFormData] = useState(data);
  const [columns, setColumns] = useState([]);
  const [tableConfig, setTableConfig] = useState(null);
  
  // Table view mode state
  const [tableRows, setTableRows] = useState(isTableViewMode ? tableData : []);
  
  // Dual table mode state
  const [parentData, setParentData] = useState(isDualTableMode ? (defaultParentData || {}) : {});
  const [childData, setChildData] = useState([]);
  const [parentConfig, setParentConfig] = useState(null);
  const [childConfig, setChildConfig] = useState(null);
  const [newChildRecord, setNewChildRecord] = useState({});
  const [isResizing, setIsResizing] = useState(false);
  const [resizeData, setResizeData] = useState(null);
  
  // Key column preservation state
  const [reservedKeyColumns, setReservedKeyColumns] = useState({});
  const [keyColumnConflicts, setKeyColumnConflicts] = useState([]);
  
  // Common state
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [errors, setErrors] = useState({});
  const [columnCount, setColumnCount] = useState(columnsPerRow);
  const [globalSettings, setGlobalSettings] = useState({});
  
  // File upload states
  const [droppedFile, setDroppedFile] = useState(null);
  
  // Debug states
  const [debugInfo, setDebugInfo] = useState(null);
  const [showDebugPanel, setShowDebugPanel] = useState(false);
  
  const dropZoneRef = useRef(null);
  const dataFormRef = useRef(null);

  // Custom calculation handler
  const handleCustomCalculate = useCallback(async (column, rowIndex = null) => {
    const func = customCalculations[column.calculationFunction];
    if (!func) return;
    try {
      if (viewMode === 'form') {
        const result = await func({ selectedWellId, reportDate, currentReportId, sectionEditingData: formData });
        setFormData(prev => ({ ...prev, [column.name]: result }));
      } else {
        const result = await func({ selectedWellId, reportDate, currentReportId, sectionEditingData: tableRows[rowIndex] });
        setTableRows(prev => prev.map((row, idx) => idx === rowIndex ? { ...row, [column.name]: result } : row));
      }
    } catch (error) {
      console.error('Calculation error:', error);
      alert(`Calculation failed for ${column.displayName || column.name}`);
    }
  }, [selectedWellId, reportDate, currentReportId, viewMode, formData, tableRows]);

  // Load column configurations
  useEffect(() => {
    const loadConfigurations = async () => {
      setIsLoading(true);
      setError(null);
      try {
        if (sectionConfig) {
          // Table view mode - use sectionConfig fields directly
          if (sectionConfig && Array.isArray(sectionConfig.fields)) {
            const enabledFields = sectionConfig.fields
              .filter(field => field.enabled)
              .sort((a, b) => (a.order || 0) - (b.order || 0));
            
            // Convert sectionConfig fields to column format
            const tableColumns = enabledFields.map(field => ({
              name: field.dbColumn,
              displayName: field.name,
              label: field.name,
              type: field.format || 'text',
              format: field.format || 'text',
              timeFormat: field.timeFormat,
              width: field.width,
              enabled: true,
              order: field.order || 0,
              required: field.required || false,
              placeholder: field.name,
              // Include lookup configuration if present
              options: field.options,
              lookupTable: field.lookupTable,
              lookupValueColumn: field.lookupValueColumn,
              lookupDisplayColumn: field.lookupDisplayColumn,
              lookupDbSchema: field.lookupDbSchema,
              // Include custom calculation function if configured
              calculationFunction: field.calculationFunction
            }));
            
            setColumns(tableColumns);
            console.log('📊 Table view columns loaded:', tableColumns);
          } else {
            throw new Error('Section configuration not provided for table view mode');
          }
        } else {
          // Original logic for single/dual table modes
          const response = await apiFetch('/api/config/columns');
          const allConfigs = Array.isArray(response) ? response : [];
          
          if (isSingleTableMode) {
            // Single table mode - load one configuration
            const foundTableConfig = allConfigs.find(config => config.tableName === tableName);
            if (foundTableConfig && Array.isArray(foundTableConfig.columns)) {
              const sortedColumns = foundTableConfig.columns.sort((a, b) => a.order - b.order);
              setColumns(sortedColumns);
              setTableConfig(foundTableConfig);
            } else {
              throw new Error(`Table configuration for '${tableName}' not found`);
            }
          } else if (isDualTableMode) {
            // Dual table mode - load parent and child configurations
            const parentTableConfig = allConfigs.find(config => config.tableName === tables.parent);
            const childTableConfig = allConfigs.find(config => config.tableName === tables.child);
            
            setParentConfig(parentTableConfig);
            setChildConfig(childTableConfig);
          }
        }
      } catch (err) {
        console.error('Failed to fetch column config:', err);
        setError(err.message || 'Failed to load form configuration');
      }
      setIsLoading(false);
    };

    // Always load when sectionConfig is provided, fallback to legacy otherwise
    if (sectionConfig || isSingleTableMode || isDualTableMode) {
      loadConfigurations();
    }
  }, [tableName, tables.parent, tables.child, isSingleTableMode, isDualTableMode, sectionConfig]);

  // Handle data loading for both modes
  useEffect(() => {
    if (isSingleTableMode) {
      console.log('📝 DataForm data prop changed:', {
        tableName,
        newData: data,
        hasData: data && Object.keys(data).length > 0
      });
      setFormData(data);
      setError(null);
    }
  }, [data, tableName, isSingleTableMode]);

  // Update table rows when tableData prop changes (for table view mode)
  useEffect(() => {
    if (isTableViewMode) {
      console.log('📊 DataForm tableData prop changed:', {
        tableName,
        newTableData: tableData,
        rowCount: tableData?.length || 0
      });
      setTableRows(Array.isArray(tableData) ? tableData : []);
      setError(null);
    }
  }, [tableData, tableName, isTableViewMode]);

  // Load data for dual table mode when recordId changes
  useEffect(() => {
    const loadData = async () => {
      if (!recordId || !tables.parent || !tables.child) return;

      console.log(`🔄 Loading data for ${tables.parent} ID: ${recordId}`);
      setIsLoading(true);
      try {
        // Load parent record
        console.log(`📡 Fetching parent record: /api/db/${tables.parent}/${recordId}`);
        const parentResponse = await apiFetch(`/api/db/${tables.parent}/${recordId}`);
        console.log('📄 Parent data loaded:', parentResponse);
        
        // Merge with default parent data to ensure all required fields are present
        const mergedParentData = { ...defaultParentData, ...(parentResponse || {}) };
        setParentData(mergedParentData);

        // Load child records
        console.log(`📡 Fetching child records: /api/db/${tables.child}?filter=${matchKey.childForeignKey},eq,${recordId}`);
        const childResponse = await apiFetch(`/api/db/${tables.child}?filter=${matchKey.childForeignKey},eq,${recordId}`);
        console.log('📄 Child data loaded:', childResponse);
        setChildData(Array.isArray(childResponse) ? childResponse : []);
      } catch (error) {
        console.error('❌ Failed to load data:', error);
        setErrors({ general: 'Failed to load data: ' + error.message });
      } finally {
        setIsLoading(false);
      }
    };

    if (isDualTableMode) {
      loadData();
    }
  }, [recordId, tables.parent, tables.child, matchKey.childForeignKey, defaultParentData, isDualTableMode]);

  // Initialize empty parent data for new records in dual table mode
  useEffect(() => {
    if (isDualTableMode && !recordId && parentConfig) {
      const emptyParent = { ...defaultParentData };
      if (parentConfig.columns) {
        parentConfig.columns.forEach(col => {
          if (col.name !== identityColumns.parent && !emptyParent.hasOwnProperty(col.name)) {
            emptyParent[col.name] = '';
          }
        });
      }
      setParentData(emptyParent);
    }
  }, [recordId, parentConfig, identityColumns.parent, defaultParentData, isDualTableMode]);

  // Initialize reserved key columns from defaultParentData
  useEffect(() => {
    if (isDualTableMode && defaultParentData && Object.keys(defaultParentData).length > 0) {
      console.log('🔒 Initializing reserved key columns from defaultParentData:', defaultParentData);
      setReservedKeyColumns(defaultParentData);
    }
  }, [isDualTableMode, defaultParentData]);

  // Update column count when prop changes
  useEffect(() => {
    setColumnCount(columnsPerRow);
  }, [columnsPerRow]);

  // Load global units settings
  useEffect(() => {
    const loadGlobalSettings = async () => {
      try {
        const settings = await getGlobalUnitSettings();
        if (settings) {
          setGlobalSettings(settings);
        }
      } catch (error) {
        console.error('Failed to load global unit settings:', error);
      }
    };
    loadGlobalSettings();
  }, []);

  // Handle resize operations to maintain drop zone coverage
  useEffect(() => {
    const dataFormElement = dataFormRef.current;
    if (!dataFormElement) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const element = entry.target;
        // Add data attribute to indicate resizing state
        element.setAttribute('data-resizing', 'true');
        
        // Clear the resizing state after animation frame
        requestAnimationFrame(() => {
          setTimeout(() => {
            element.setAttribute('data-resizing', 'false');
          }, 100);
        });
      }
    });

    resizeObserver.observe(dataFormElement);

    return () => {
      resizeObserver.unobserve(dataFormElement);
      resizeObserver.disconnect();
    };
  }, []);

  // Single table mode handlers
  const handleChange = (columnName, value) => {
    if (isSingleTableMode) {
      setFormData(prev => ({ ...prev, [columnName]: value }));
    }
  };

  // Multi-field change handler for single table mode (for lookup fields with lookupReturnColumn)
  const handleMultiFieldChange = (fieldMappings) => {
    if (isSingleTableMode) {
      console.log('🔄 DataForm: Multi-field change in single table mode:', fieldMappings);
      setFormData(prev => ({ ...prev, ...fieldMappings }));
    }
  };

  // Table view mode handlers
  const handleTableFieldChange = (rowIndex, columnName, value) => {
    if (isTableViewMode) {
      setTableRows(prev => {
        const newRows = [...prev];
        if (newRows[rowIndex]) {
          newRows[rowIndex] = { ...newRows[rowIndex], [columnName]: value };
        }
        return newRows;
      });
    }
  };

  // Multi-field change handler for table view mode (for lookup fields with lookupReturnColumn)
  const handleTableMultiFieldChange = (rowIndex, fieldMappings) => {
    if (isTableViewMode) {
      console.log('🔄 DataForm: Multi-field change in table view mode:', { rowIndex, fieldMappings });
      setTableRows(prev => {
        const newRows = [...prev];
        if (newRows[rowIndex]) {
          newRows[rowIndex] = { ...newRows[rowIndex], ...fieldMappings };
        }
        return newRows;
      });
    }
  };

  const handleAddTableRow = () => {
    if (isTableViewMode && sectionConfig) {
      const emptyRow = {};
      sectionConfig.fields.forEach(field => {
        if (field.enabled && field.dbColumn) {
          emptyRow[field.dbColumn] = '';
        }
      });
      setTableRows(prev => [...prev, emptyRow]);
    }
  };

  const handleRemoveTableRow = (rowIndex) => {
    if (isTableViewMode) {
      setTableRows(prev => prev.filter((_, index) => index !== rowIndex));
    }
  };

  // Dual table mode handlers
  const handleParentChange = useCallback((fieldName, value) => {
    setParentData(prev => ({
      ...prev,
      [fieldName]: value
    }));
    
    // Clear field-specific errors
    if (errors[fieldName]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  }, [errors]);

  const handleChildChange = useCallback((index, fieldName, value) => {
    setChildData(prev => {
      const newData = [...prev];
      newData[index] = {
        ...newData[index],
        [fieldName]: value
      };
      return newData;
    });
  }, []);

  const handleNewChildChange = useCallback((fieldName, value) => {
    console.log(`📝 New child field changed: ${fieldName} = "${value}"`);
    setNewChildRecord(prev => {
      const updated = {
        ...prev,
        [fieldName]: value
      };
      console.log('Updated newChildRecord:', updated);
      return updated;
    });
  }, []);

  const handleAddChild = useCallback(() => {
    if (!childConfig) {
      console.log('❌ No child config available');
      return;
    }

    console.log('🔍 Attempting to add child record...');
    console.log('Current newChildRecord:', newChildRecord);
    console.log('recordId:', recordId);
    console.log('parentData:', parentData);
    console.log('matchKey:', matchKey);

    // Check if the new record has any meaningful data
    const hasData = Object.keys(newChildRecord).some(key => {
      const value = newChildRecord[key];
      return value !== null && value !== undefined && value !== '';
    });

    if (!hasData) {
      console.log('❌ Cannot add empty child record - no meaningful data found');
      console.log('Available keys in newChildRecord:', Object.keys(newChildRecord));
      return;
    }

    const newRecord = { ...newChildRecord };
    // Set foreign key to link to parent
    const foreignKeyValue = recordId || parentData[matchKey.parentKey];
    newRecord[matchKey.childForeignKey] = foreignKeyValue;
    
    console.log('➕ Adding new child record:', newRecord);
    console.log('Foreign key set:', matchKey.childForeignKey, '=', foreignKeyValue);
    console.log('Current childData before adding:', childData);
    
    setChildData(prev => {
      const updated = [...prev, newRecord];
      console.log('Updated childData:', updated);
      return updated;
    });
    setNewChildRecord({});
    console.log('✅ Child record added to state, newChildRecord cleared');
  }, [newChildRecord, childConfig, matchKey, recordId, parentData, childData]);

  const handleRemoveChild = useCallback((index) => {
    setChildData(prev => prev.filter((_, i) => i !== index));
  }, []);

  // Handle file drop - simplified for DataExtraction component
  const handleFileDrop = useCallback((file) => {
    console.log('📁 File dropped:', file.name);
    setDroppedFile(file);
  }, []);

  // Helper function to preserve key columns and detect conflicts
  const preserveKeyColumns = useCallback((extractedData, reservedKeys) => {
    if (!reservedKeys || Object.keys(reservedKeys).length === 0) {
      return { data: extractedData, conflicts: [] };
    }

    const conflicts = [];
    const preservedData = { ...extractedData };

    // Check for conflicts and preserve reserved key columns
    Object.keys(reservedKeys).forEach(keyColumn => {
      const reservedValue = reservedKeys[keyColumn];
      const extractedValue = extractedData[keyColumn];

      if (extractedValue !== undefined && extractedValue !== null && extractedValue !== '') {
        if (String(extractedValue) !== String(reservedValue)) {
          // Conflict detected - extracted value differs from reserved value
          conflicts.push({
            field: keyColumn,
            reservedValue,
            extractedValue,
            message: `Key column "${keyColumn}" conflict: Page value "${reservedValue}" vs Extracted value "${extractedValue}". Using page value.`
          });
        }
      }

      // Always preserve the reserved key column value
      preservedData[keyColumn] = reservedValue;
    });

    return { data: preservedData, conflicts };
  }, []);

  // Handle data extraction results
  const handleDataExtracted = useCallback((extractionResult) => {
    console.log('📊 Data extraction result:', extractionResult);
    
    const { type, data, mode } = extractionResult;
    
    if (type === 'openai') {
      // Handle OpenAI extraction results
      if (isSingleTableMode) {
        setFormData(prev => ({ ...prev, ...data }));
      } else if (isDualTableMode) {
        const newParentData = { ...parentData };
        const newChildRecords = [];
        
        Object.entries(data).forEach(([fieldName, value]) => {
          const parentColumn = parentConfig?.columns?.find(col => col.name === fieldName);
          const childColumn = childConfig?.columns?.find(col => col.name === fieldName);
          
          if (parentColumn && !Array.isArray(value)) {
            newParentData[parentColumn.name] = value;
          } else if (childColumn) {
            if (Array.isArray(value)) {
              value.forEach(val => {
                const newChildRow = { [childColumn.name]: val };
                if (matchKey?.childForeignKey && parentData[matchKey.parentKey]) {
                  newChildRow[matchKey.childForeignKey] = parentData[matchKey.parentKey];
                }
                newChildRecords.push(newChildRow);
              });
            } else {
              const newChildRow = { [childColumn.name]: value };
              if (matchKey?.childForeignKey && parentData[matchKey.parentKey]) {
                newChildRow[matchKey.childForeignKey] = parentData[matchKey.parentKey];
              }
              newChildRecords.push(newChildRow);
            }
          }
        });
        
        setParentData(newParentData);
        setChildData(prev => [...prev, ...newChildRecords]);
      } else if (isTableViewMode) {
        const newRows = Array.isArray(data) ? data : [data];
        setTableRows(prev => [...prev, ...newRows]);
      }
    } else if (type === 'excel') {
      // Handle Excel mapping extraction
      if (isSingleTableMode) {
        const newFormData = { ...formData };
        Object.entries(data).forEach(([label, value]) => {
          const matchingColumn = columns.find(col => 
            col.name.toLowerCase() === label.toLowerCase() ||
            col.displayName?.toLowerCase() === label.toLowerCase() ||
            col.label?.toLowerCase() === label.toLowerCase() ||
            col.name.toLowerCase().includes(label.toLowerCase()) ||
            label.toLowerCase().includes(col.name.toLowerCase())
          );
          
          if (matchingColumn && value) {
            newFormData[matchingColumn.name] = value;
          }
        });
        setFormData(newFormData);
      } else if (isDualTableMode) {
        // Handle dual table mode Excel extraction
        const newParentData = { ...parentData };
        const newChildData = [...childData];
        
        Object.entries(data).forEach(([label, value]) => {
          const parentColumn = parentConfig?.columns?.find(col => 
            col.name.toLowerCase() === label.toLowerCase() ||
            col.displayName?.toLowerCase() === label.toLowerCase() ||
            col.label?.toLowerCase() === label.toLowerCase()
          );
          
          const childColumn = childConfig?.columns?.find(col => 
            col.name.toLowerCase() === label.toLowerCase() ||
            col.displayName?.toLowerCase() === label.toLowerCase() ||
            col.label?.toLowerCase() === label.toLowerCase()
          );
          
          if (parentColumn && value) {
            newParentData[parentColumn.name] = value;
          } else if (childColumn && value) {
            const newChildRow = { [childColumn.name]: value };
            if (matchKey?.childForeignKey && parentData[matchKey.parentKey]) {
              newChildRow[matchKey.childForeignKey] = parentData[matchKey.parentKey];
            }
            newChildData.push(newChildRow);
          }
        });
        
        setParentData(newParentData);
        setChildData(newChildData);
      }
    } else if (type === 'smartExcel') {
      // Handle Smart Excel extraction
      if (isSingleTableMode) {
        setFormData(prev => ({ ...prev, ...data }));
      } else if (isDualTableMode) {
        const newParentData = { ...parentData };
        const newChildData = [...childData];
        
        Object.entries(data).forEach(([fieldName, value]) => {
          const parentColumn = parentConfig?.columns?.find(col => 
            col.name === fieldName || col.displayName === fieldName || col.label === fieldName
          );
          const childColumn = childConfig?.columns?.find(col => 
            col.name === fieldName || col.displayName === fieldName || col.label === fieldName
          );
          
          if (parentColumn && value) {
            newParentData[parentColumn.name] = value;
          } else if (childColumn && value) {
            const newChildRow = { [childColumn.name]: value };
            if (matchKey?.childForeignKey && parentData[matchKey.parentKey]) {
              newChildRow[matchKey.childForeignKey] = parentData[matchKey.parentKey];
            }
            newChildData.push(newChildRow);
          }
        });
        
        setParentData(newParentData);
        setChildData(newChildData);
      } else if (isTableViewMode) {
        const newRow = { ...data };
        setTableRows(prev => [...prev, newRow]);
      }
    } else if (type === 'readToForm') {
      // Handle Read to Form extraction
      if (mode === 'labelValue') {
        if (isTableViewMode) {
          setTableRows([data]);
        } else {
          setFormData(prev => ({ ...prev, ...data }));
        }
      } else if (mode === 'table') {
        setTableRows(data);
      }
    } else if (type === 'pdfOpenAI' || type === 'imageOpenAI' || type === 'pdfTextOpenAI' || type === 'excelImageOpenAI') {
      // Handle AI extraction results with enhanced table data processing
      console.log('📊 Processing AI extraction data:', data);
      
      if (isSingleTableMode) {
        // For single table mode, merge all non-table data
        const singleData = { ...data };
        delete singleData.tableData; // Remove table data if present
        setFormData(prev => ({ ...prev, ...singleData }));
      } else if (isDualTableMode) {
        const newParentData = { ...parentData };
        const newChildRecords = [];
        
        // Separate parent and child data from extraction
        const parentExtractedData = {};
        const childExtractedData = {};
        
        Object.entries(data).forEach(([fieldName, value]) => {
          if (value === null || value === undefined || value === '') return;
          
          // Skip table data processing for dual table mode
          if (fieldName === 'tableData') return;
          
          const parentColumn = parentConfig?.columns?.find(col => 
            col.name === fieldName || col.displayName === fieldName || col.label === fieldName
          );
          const childColumn = childConfig?.columns?.find(col => 
            col.name === fieldName || col.displayName === fieldName || col.label === fieldName
          );
          
          if (parentColumn && !Array.isArray(value)) {
            parentExtractedData[parentColumn.name] = value;
          } else if (childColumn) {
            childExtractedData[fieldName] = value;
          }
        });
        
        // Apply key column preservation to parent data
        const { data: preservedParentData, conflicts: parentConflicts } = preserveKeyColumns(parentExtractedData, reservedKeyColumns);
        
        // Update parent data with preserved key columns
        Object.assign(newParentData, preservedParentData);
        
        // Handle conflicts
        if (parentConflicts.length > 0) {
          console.log('⚠️ Key column conflicts detected:', parentConflicts);
          setKeyColumnConflicts(prev => [...prev, ...parentConflicts]);
          
          // Show notification to user
          const conflictMessages = parentConflicts.map(conflict => conflict.message);
          console.warn('🔒 Key column conflicts:', conflictMessages.join('\n'));
        }
        
        // Process child data
        Object.entries(childExtractedData).forEach(([fieldName, value]) => {
          const childColumn = childConfig?.columns?.find(col => 
            col.name === fieldName || col.displayName === fieldName || col.label === fieldName
          );
          
          if (childColumn) {
            if (Array.isArray(value)) {
              // Handle array data - could be table rows or individual values
              if (value.length > 0 && typeof value[0] === 'object' && value[0] !== null) {
                // Array of objects - treat as table rows
                value.forEach(rowData => {
                  const newChildRow = {};
                  Object.entries(rowData).forEach(([key, val]) => {
                    const matchingColumn = childConfig?.columns?.find(col => 
                      col.name === key || col.displayName === key || col.label === key
                    );
                    if (matchingColumn) {
                      newChildRow[matchingColumn.name] = val;
                    }
                  });
                  if (matchKey?.childForeignKey && parentData[matchKey.parentKey]) {
                    newChildRow[matchKey.childForeignKey] = parentData[matchKey.parentKey];
                  }
                  newChildRecords.push(newChildRow);
                });
              } else {
                // Array of simple values
                value.forEach(val => {
                  const newChildRow = { [childColumn.name]: val };
                  if (matchKey?.childForeignKey && parentData[matchKey.parentKey]) {
                    newChildRow[matchKey.childForeignKey] = parentData[matchKey.parentKey];
                  }
                  newChildRecords.push(newChildRow);
                });
              }
            } else {
              const newChildRow = { [childColumn.name]: value };
              if (matchKey?.childForeignKey && parentData[matchKey.parentKey]) {
                newChildRow[matchKey.childForeignKey] = parentData[matchKey.parentKey];
              }
              newChildRecords.push(newChildRow);
            }
          }
        });
        
        setParentData(newParentData);
        setChildData(prev => [...prev, ...newChildRecords]);
      } else if (isTableViewMode) {
        // Handle table view mode with enhanced table data processing
        if (data.tableData && Array.isArray(data.tableData)) {
          // Use processed table data
          setTableRows(prev => [...prev, ...data.tableData]);
        } else {
          // Fallback to single row
          const newRow = { ...data };
          delete newRow.tableData; // Remove table data if present
          setTableRows(prev => [...prev, newRow]);
        }
      }
    } else if (type === 'pdfTextOpenAI') {
      // Handle PDF Text OpenAI extraction results (fallback method)
      if (isSingleTableMode) {
        setFormData(prev => ({ ...prev, ...data }));
      } else if (isDualTableMode) {
        const newParentData = { ...parentData };
        const newChildRecords = [];
        
        // Separate parent and child data from extraction
        const parentExtractedData = {};
        const childExtractedData = {};
        
        Object.entries(data).forEach(([fieldName, value]) => {
          if (value === null || value === undefined || value === '') return;
          
          const parentColumn = parentConfig?.columns?.find(col => 
            col.name === fieldName || col.displayName === fieldName || col.label === fieldName
          );
          const childColumn = childConfig?.columns?.find(col => 
            col.name === fieldName || col.displayName === fieldName || col.label === fieldName
          );
          
          if (parentColumn && !Array.isArray(value)) {
            parentExtractedData[parentColumn.name] = value;
          } else if (childColumn) {
            childExtractedData[fieldName] = value;
          }
        });
        
        // Apply key column preservation to parent data
        const { data: preservedParentData, conflicts: parentConflicts } = preserveKeyColumns(parentExtractedData, reservedKeyColumns);
        
        // Update parent data with preserved key columns
        Object.assign(newParentData, preservedParentData);
        
        // Handle conflicts
        if (parentConflicts.length > 0) {
          console.log('⚠️ Key column conflicts detected (pdfTextOpenAI):', parentConflicts);
          setKeyColumnConflicts(prev => [...prev, ...parentConflicts]);
          
          // Show notification to user
          const conflictMessages = parentConflicts.map(conflict => conflict.message);
          console.warn('🔒 Key column conflicts (pdfTextOpenAI):', conflictMessages.join('\n'));
        }
        
        // Process child data
        Object.entries(childExtractedData).forEach(([fieldName, value]) => {
          const childColumn = childConfig?.columns?.find(col => 
            col.name === fieldName || col.displayName === fieldName || col.label === fieldName
          );
          
          if (childColumn) {
            if (Array.isArray(value)) {
              value.forEach(val => {
                const newChildRow = { [childColumn.name]: val };
                if (matchKey?.childForeignKey && parentData[matchKey.parentKey]) {
                  newChildRow[matchKey.childForeignKey] = parentData[matchKey.parentKey];
                }
                newChildRecords.push(newChildRow);
              });
            } else {
              const newChildRow = { [childColumn.name]: value };
              if (matchKey?.childForeignKey && parentData[matchKey.parentKey]) {
                newChildRow[matchKey.childForeignKey] = parentData[matchKey.parentKey];
              }
              newChildRecords.push(newChildRow);
            }
          }
        });
        
        setParentData(newParentData);
        setChildData(prev => [...prev, ...newChildRecords]);
      } else if (isTableViewMode) {
        const newRow = { ...data };
        setTableRows(prev => [...prev, newRow]);
      }
    } else if (type === 'excelImageOpenAI') {
      // Handle Excel Image OpenAI extraction results
      if (isSingleTableMode) {
        setFormData(prev => ({ ...prev, ...data }));
      } else if (isDualTableMode) {
        const newParentData = { ...parentData };
        const newChildRecords = [];
        
        // Separate parent and child data from extraction
        const parentExtractedData = {};
        const childExtractedData = {};
        
        Object.entries(data).forEach(([fieldName, value]) => {
          if (value === null || value === undefined || value === '') return;
          
          const parentColumn = parentConfig?.columns?.find(col => 
            col.name === fieldName || col.displayName === fieldName || col.label === fieldName
          );
          const childColumn = childConfig?.columns?.find(col => 
            col.name === fieldName || col.displayName === fieldName || col.label === fieldName
          );
          
          if (parentColumn && !Array.isArray(value)) {
            parentExtractedData[parentColumn.name] = value;
          } else if (childColumn) {
            childExtractedData[fieldName] = value;
          }
        });
        
        // Apply key column preservation to parent data
        const { data: preservedParentData, conflicts: parentConflicts } = preserveKeyColumns(parentExtractedData, reservedKeyColumns);
        
        // Update parent data with preserved key columns
        Object.assign(newParentData, preservedParentData);
        
        // Handle conflicts
        if (parentConflicts.length > 0) {
          console.log('⚠️ Key column conflicts detected (excelImageOpenAI):', parentConflicts);
          setKeyColumnConflicts(prev => [...prev, ...parentConflicts]);
          
          // Show notification to user
          const conflictMessages = parentConflicts.map(conflict => conflict.message);
          console.warn('🔒 Key column conflicts (excelImageOpenAI):', conflictMessages.join('\n'));
        }
        
        // Process child data
        Object.entries(childExtractedData).forEach(([fieldName, value]) => {
          const childColumn = childConfig?.columns?.find(col => 
            col.name === fieldName || col.displayName === fieldName || col.label === fieldName
          );
          
          if (childColumn) {
            if (Array.isArray(value)) {
              value.forEach(val => {
                const newChildRow = { [childColumn.name]: val };
                if (matchKey?.childForeignKey && parentData[matchKey.parentKey]) {
                  newChildRow[matchKey.childForeignKey] = parentData[matchKey.parentKey];
                }
                newChildRecords.push(newChildRow);
              });
            } else {
              const newChildRow = { [childColumn.name]: value };
              if (matchKey?.childForeignKey && parentData[matchKey.parentKey]) {
                newChildRow[matchKey.childForeignKey] = parentData[matchKey.parentKey];
              }
              newChildRecords.push(newChildRow);
            }
          }
        });
        
        setParentData(newParentData);
        setChildData(prev => [...prev, ...newChildRecords]);
      } else if (isTableViewMode) {
        const newRow = { ...data };
        setTableRows(prev => [...prev, newRow]);
      }
    }
  }, [isSingleTableMode, isDualTableMode, isTableViewMode, formData, parentData, parentConfig, childConfig, matchKey, columns, preserveKeyColumns, reservedKeyColumns]);

  // Handle attachment saved
  const handleAttachmentSaved = useCallback((attachmentData) => {
    console.log('📎 Attachment saved:', attachmentData);
  }, []);

  // Handle file processed (cleanup)
  const handleFileProcessed = useCallback(() => {
    setDroppedFile(null);
  }, []);

  // Debug handler
  const handleDebugInfoChanged = useCallback((debugData) => {
    // Enhance debug info with key column preservation information
    const enhancedDebugData = {
      ...debugData,
      keyColumnPreservation: {
        reservedKeyColumns,
        conflictsCount: keyColumnConflicts.length,
        conflicts: keyColumnConflicts
      }
    };
    setDebugInfo(enhancedDebugData);
  }, [reservedKeyColumns, keyColumnConflicts]);

  // Clear key column conflicts
  const clearKeyColumnConflicts = useCallback(() => {
    setKeyColumnConflicts([]);
  }, []);



  // Key Column Conflicts Panel Component
  const KeyColumnConflictsPanel = ({ conflicts, onClose }) => {
    if (!conflicts || conflicts.length === 0) return null;

    return (
      <div className="key-conflicts-panel">
        <div className="key-conflicts-header">
          <h4>🔒 Key Column Conflicts Detected</h4>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>
        <div className="key-conflicts-content">
          <p className="key-conflicts-intro">
            The following key columns were preserved from the page and conflicts were resolved:
          </p>
          <ul className="key-conflicts-list">
            {conflicts.map((conflict, index) => (
              <li key={index} className="key-conflict-item">
                <div className="conflict-field">
                  <strong>{conflict.field}</strong>
                </div>
                <div className="conflict-details">
                  <div className="conflict-values">
                    <span className="reserved-value">Page Value: <strong>{conflict.reservedValue}</strong></span>
                    <span className="extracted-value">Extracted Value: <strong>{conflict.extractedValue}</strong></span>
                  </div>
                  <div className="conflict-resolution">
                    ✅ Using page value to maintain data integrity
                  </div>
                </div>
              </li>
            ))}
          </ul>
          <div className="key-conflicts-footer">
            <p>
              <strong>Note:</strong> Key columns passed from the page (like WellID) are always preserved 
              to maintain data relationships. If you need to change these values, please do so from the main page.
            </p>
          </div>
        </div>
      </div>
    );
  };

  // Debug Panel Component
  const DebugPanel = ({ debugInfo, onClose }) => {
    const [activeTab, setActiveTab] = useState('input');
    
    if (!debugInfo) return null;

    return (
      <div className="debug-panel-overlay">
        <div className="debug-panel">
          <div className="debug-panel-header">
            <h3>🔍 AI Extraction Debug Review</h3>
            <div className="debug-panel-meta">
              <span className="debug-timestamp">
                {new Date(debugInfo.timestamp).toLocaleString()}
              </span>
              <span className="debug-method">
                Method: {debugInfo.method}
              </span>
              {debugInfo.confidence && (
                <span className="debug-confidence">
                  Confidence: {(debugInfo.confidence * 100).toFixed(1)}%
                </span>
              )}
            </div>
            <button 
              className="debug-close-btn"
              onClick={onClose}
            >
              ✕
            </button>
          </div>
          
          <div className="debug-panel-tabs">
            <button 
              className={`debug-tab ${activeTab === 'input' ? 'active' : ''}`}
              onClick={() => setActiveTab('input')}
            >
              📤 Input Message
            </button>
            <button 
              className={`debug-tab ${activeTab === 'output' ? 'active' : ''}`}
              onClick={() => setActiveTab('output')}
            >
              📥 AI Response
            </button>
            <button 
              className={`debug-tab ${activeTab === 'extracted' ? 'active' : ''}`}
              onClick={() => setActiveTab('extracted')}
            >
              📊 Extracted Data
            </button>
            {debugInfo.metadata && (
              <button 
                className={`debug-tab ${activeTab === 'metadata' ? 'active' : ''}`}
                onClick={() => setActiveTab('metadata')}
              >
                📋 Metadata
              </button>
            )}
            {debugInfo.keyColumnPreservation && (
              <button 
                className={`debug-tab ${activeTab === 'keys' ? 'active' : ''}`}
                onClick={() => setActiveTab('keys')}
              >
                🔒 Key Columns
              </button>
            )}
          </div>
          
          <div className="debug-panel-content">
            {activeTab === 'input' && (
              <div className="debug-section">
                <h4>Input Message Sent to AI:</h4>
                <div className="debug-content-box">
                  <pre>{debugInfo.inputMessage}</pre>
                </div>
                {debugInfo.fieldDefinitions && (
                  <div className="debug-field-definitions">
                    <h4>Field Definitions:</h4>
                    <div className="debug-content-box">
                      <pre>{JSON.stringify(debugInfo.fieldDefinitions, null, 2)}</pre>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {activeTab === 'output' && (
              <div className="debug-section">
                <h4>Raw AI Response:</h4>
                <div className="debug-content-box">
                  <pre>{JSON.stringify(debugInfo.rawResponse, null, 2)}</pre>
                </div>
              </div>
            )}
            
            {activeTab === 'extracted' && (
              <div className="debug-section">
                <h4>Processed Extracted Data:</h4>
                <div className="debug-content-box">
                  <pre>{JSON.stringify(debugInfo.extractedData, null, 2)}</pre>
                </div>
                {debugInfo.extractionNotes && (
                  <div className="debug-notes">
                    <h4>Extraction Notes:</h4>
                    <div className="debug-content-box">
                      <pre>{JSON.stringify(debugInfo.extractionNotes, null, 2)}</pre>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {activeTab === 'metadata' && debugInfo.metadata && (
              <div className="debug-section">
                <h4>Extraction Metadata:</h4>
                <div className="debug-content-box">
                  <pre>{JSON.stringify(debugInfo.metadata, null, 2)}</pre>
                </div>
              </div>
            )}
            
            {activeTab === 'keys' && debugInfo.keyColumnPreservation && (
              <div className="debug-section">
                <h4>Key Column Preservation:</h4>
                <div className="debug-content-box">
                  <h5>Reserved Key Columns:</h5>
                  <pre>{JSON.stringify(debugInfo.keyColumnPreservation.reservedKeyColumns, null, 2)}</pre>
                  
                  <h5>Conflicts Detected: {debugInfo.keyColumnPreservation.conflictsCount}</h5>
                  {debugInfo.keyColumnPreservation.conflicts.length > 0 && (
                    <pre>{JSON.stringify(debugInfo.keyColumnPreservation.conflicts, null, 2)}</pre>
                  )}
                  
                  <div className="debug-notes">
                    <h5>Key Column Preservation Notes:</h5>
                    <ul>
                      <li>Key columns are preserved from the page's defaultParentData</li>
                      <li>Conflicts occur when AI extraction returns different values for key columns</li>
                      <li>Page values always take precedence to maintain data integrity</li>
                      <li>Conflicts are logged and displayed to the user via the conflicts panel</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          <div className="debug-panel-footer">
            <div className="debug-stats">
              <span>File: {debugInfo.fileName}</span>
              <span>Fields Processed: {debugInfo.fieldCount || 'N/A'}</span>
              <span>Processing Time: {debugInfo.processingTime || 'N/A'}ms</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Add event listener to catch all form submissions
  useEffect(() => {
    const formElement = document.getElementById('data-form');
    if (formElement) {
      const submitHandler = async (e) => {
        console.log('🔥 DEBUG: Form submission event detected');
        console.log('🔥 DEBUG: Event:', e);
        console.log('🔥 DEBUG: Event type:', e.type);
        console.log('🔥 DEBUG: Event target:', e.target);
        console.log('🔥 DEBUG: Form element:', formElement);
        console.log('🔥 DEBUG: Call stack:', new Error().stack);
        
        // Check if sheet selector is visible
        const sheetSelectorModal = document.querySelector('.data-mapping-excel-overlay');
        const excelSheetSelectorModal = document.querySelector('.excel-sheet-selector-overlay');
        if (sheetSelectorModal || excelSheetSelectorModal) {
          console.log('🔥 DEBUG: PREVENTING form submission - sheet selector is visible');
          console.log('🔥 DEBUG: Found sheet selector modal:', !!sheetSelectorModal);
          console.log('🔥 DEBUG: Found excel sheet selector modal:', !!excelSheetSelectorModal);
          e.preventDefault();
          e.stopPropagation();
          return false;
        }
      };
      
      formElement.addEventListener('submit', submitHandler, true); // Use capture phase
      
      return () => {
        formElement.removeEventListener('submit', submitHandler, true);
      };
    }
  }, [droppedFile]);

  const handleSubmit = async (e) => {
    console.log('🔥 DEBUG: handleSubmit called');
    console.log('🔥 DEBUG: Event:', e);
    console.log('🔥 DEBUG: Event type:', e?.type);
    console.log('🔥 DEBUG: Event target:', e?.target);
    console.log('🔥 DEBUG: Call stack:', new Error().stack);
    
    // Check if Excel sheet selector is visible - if so, prevent submission
    if (droppedFile) {
      console.log('🔥 DEBUG: Dropped file present, checking for sheet selector...');
      
      // We need to check if the sheet selector modal is visible
      // This is a safety check to prevent accidental form submission during file processing
      const sheetSelectorModal = document.querySelector('.data-mapping-excel-overlay');
      const excelSheetSelectorModal = document.querySelector('.excel-sheet-selector-overlay');
      
      console.log('🔥 DEBUG: Sheet selector modal elements found:', {
        sheetSelectorModal: !!sheetSelectorModal,
        excelSheetSelectorModal: !!excelSheetSelectorModal,
        sheetSelectorVisible: sheetSelectorModal ? window.getComputedStyle(sheetSelectorModal).display !== 'none' : false,
        excelSheetSelectorVisible: excelSheetSelectorModal ? window.getComputedStyle(excelSheetSelectorModal).display !== 'none' : false
      });
      
      if (sheetSelectorModal || excelSheetSelectorModal) {
        console.log('🔥 DEBUG: Sheet selector modal is visible, preventing form submission');
        console.log('🔥 DEBUG: Found sheet selector modal:', !!sheetSelectorModal);
        console.log('🔥 DEBUG: Found excel sheet selector modal:', !!excelSheetSelectorModal);
        e.preventDefault();
        return;
      }
    }
    
    e.preventDefault();
    
    if (isSaving) {
      console.log('🔥 DEBUG: Already saving, returning early');
      return;
    }
    
    console.log('🔥 DEBUG: Starting save process');
    setIsSaving(true);
    setError(null);
    setErrors({});

    try {
      if (isTableViewMode) {
        // Table view mode - save all table rows
        console.log('💾 Saving table data:', tableRows);
        
        if (onSave) {
          await onSave(tableRows);  // Pass array directly, not wrapped in object
        }
        
        console.log('✅ Table data saved successfully');
      } else if (isDualTableMode) {
        // Dual table mode
        console.log('💾 Starting dual table save');
        await handleDualTableSave();
      } else if (isSingleTableMode) {
        // Single table mode
        console.log('💾 Saving form data:', formData);
        
        if (onSave) {
          await onSave(formData);
        }
        
        console.log('✅ Form data saved successfully');
      }
    } catch (error) {
      console.error('❌ Save failed:', error);
      setError(error.message || 'Failed to save data');
    } finally {
      console.log('🔥 DEBUG: Save process completed');
      setIsSaving(false);
    }
  };

  // Dual table mode save logic
  const handleDualTableSave = async () => {
    if (!validateDualTableData()) {
      console.log('❌ Validation failed');
      return;
    }

    console.log('💾 Starting dual table save process...');
    console.log('🔍 Current state before save:');
    console.log('- parentData:', parentData);
    console.log('- childData array length:', childData.length);
    console.log('- newChildRecord (should be empty):', newChildRecord);

    // Check if there's a pending new child record that hasn't been added yet
    const hasNewChildData = Object.keys(newChildRecord).some(key => {
      const value = newChildRecord[key];
      return value !== null && value !== undefined && value !== '';
    });

    let finalChildData = [...childData];
    
    if (hasNewChildData) {
      console.log('🔄 Found pending new child record, adding it automatically before save...');
      const newRecord = { ...newChildRecord };
      const foreignKeyValue = recordId || parentData[matchKey.parentKey];
      newRecord[matchKey.childForeignKey] = foreignKeyValue;
      finalChildData.push(newRecord);
      console.log('✅ Added pending new child record:', newRecord);
      setNewChildRecord({});
    }

    let parentId = recordId;

    // Clean payload helper function
    const cleanPayload = (data) => {
      const cleaned = {};
      Object.keys(data).forEach(key => {
        const value = data[key];
        if (value !== null && value !== undefined && value !== '') {
          cleaned[key] = value;
        }
      });
      return cleaned;
    };

    // Save parent record first
    console.log('📝 Saving parent record...');
    const cleanedParentData = cleanPayload(parentData);
    console.log('Cleaned parent data:', cleanedParentData);
    
    if (recordId) {
      // Update existing parent
      console.log(`🔄 Updating existing parent with ID: ${recordId}`);
      await apiFetch(`/api/db/${tables.parent}/${recordId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(cleanedParentData)
      });
      console.log('✅ Parent updated successfully');
    } else {
      // Create new parent
      console.log('➕ Creating new parent record');
      const parentResponse = await apiFetch(`/api/db/${tables.parent}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(cleanedParentData)
      });
      parentId = parentResponse[identityColumns.parent];
      console.log(`✅ Parent created with ID: ${parentId}`);
    }

    // Save child records
    console.log(`📝 Saving ${finalChildData.length} child records...`);
    
    for (let i = 0; i < finalChildData.length; i++) {
      const child = finalChildData[i];
      const childRecord = { ...child };
      childRecord[matchKey.childForeignKey] = parentId;

      console.log(`\n📋 Processing child record ${i + 1}/${finalChildData.length}:`);
      console.log('Original child:', child);
      console.log('Child record with FK:', childRecord);

      if (!childRecord[matchKey.childForeignKey]) {
        console.error(`❌ Child record ${i + 1} missing foreign key ${matchKey.childForeignKey}`);
        throw new Error(`Child record ${i + 1} is missing the required foreign key`);
      }

      // Check if child record has meaningful data besides the foreign key
      const dataKeys = Object.keys(childRecord).filter(key => 
        key !== matchKey.childForeignKey && key !== identityColumns.child
      );
      
      const hasData = dataKeys.some(key => {
        const value = childRecord[key];
        return value !== null && value !== undefined && value !== '';
      });

      if (!hasData) {
        console.log(`⚠️ Skipping empty child record ${i + 1} - no meaningful data`);
        continue;
      }

      if (childRecord[identityColumns.child]) {
        // Update existing child
        console.log(`🔄 Updating existing child with ID: ${childRecord[identityColumns.child]}`);
        const cleanedRecord = cleanPayload(childRecord);
        
        const response = await apiFetch(`/api/db/${tables.child}/${childRecord[identityColumns.child]}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(cleanedRecord)
        });
        console.log(`✅ Child ${i + 1} updated successfully:`, response);
      } else {
        // Create new child
        console.log(`➕ Creating new child record ${i + 1}`);
        const cleanChild = { ...childRecord };
        delete cleanChild[identityColumns.child];
        
        const cleanedChild = cleanPayload(cleanChild);
        
        const response = await apiFetch(`/api/db/${tables.child}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(cleanedChild)
        });
        console.log(`✅ Child ${i + 1} created successfully:`, response);
      }
    }

    console.log('🎉 All records saved successfully');
    if (onSave) {
      onSave({ parentId, parentData, childData: finalChildData });
    }
  };

  // Validate dual table data
  const validateDualTableData = () => {
    const newErrors = {};

    // Validate parent data
    if (parentConfig?.columns) {
      parentConfig.columns.forEach(col => {
        if (col.required && !parentData[col.name]) {
          newErrors[col.name] = `${col.label || col.name} is required`;
        }
      });
    }

    // Validate child data
    childData.forEach((child, index) => {
      if (childConfig?.columns) {
        childConfig.columns.forEach(col => {
          if (col.required && !child[col.name]) {
            newErrors[`child_${index}_${col.name}`] = `${col.label || col.name} is required`;
          }
        });
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCancel = () => {
    setFormData(data); // Reset to original data
    if (onCancel) onCancel();
  };

  const renderField = (column) => {
    const value = formData[column.name] || '';
    const colType = column.type ? column.type.toLowerCase() : 'text';
    
    // Don't render hidden fields
    if (column.hidden) return null;

    const fieldId = `field-${column.name}`;
    const isRequired = column.required || false;
    
    // Resolve unit (handle "global" units)
    const resolvedUnit = resolveFieldUnitSync(column, globalSettings);
    
    // Utility to get unique values for a column from wells data
    const getUniqueColumnValues = (columnName) => {
      if (!Array.isArray(tableData)) return [];
      const values = tableData.map(row => row[columnName]).filter(v => v != null && v !== '');
      return Array.from(new Set(values));
    };

    let inputElement;

    // Check if field is selectable
    const isSelectable = isFieldSelectable(column);
    let options = column.options || [];
    if (isSelectable && (!options || options.length === 0)) {
      // If no static options, get unique values from tableData
      options = getUniqueColumnValues(column.name);
    }

    switch (colType) {
      case 'textarea':
        inputElement = (
          <textarea
            id={fieldId}
            value={value}
            onChange={(e) => handleChange(column.name, e.target.value)}
            rows={4}
            className="form-textarea"
            placeholder={''}
            required={isRequired}
          />
        );
        break;
        
      case 'select':
        inputElement = (
          <select
            id={fieldId}
            value={value}
            onChange={(e) => handleChange(column.name, e.target.value)}
            className="form-select"
            required={isRequired}
          >
            <option value="">Select...</option>
            {options.map((option, idx) => (
              <option key={idx} value={option}>
                {option}
              </option>
            ))}
          </select>
        );
        break;
        
      case 'date':
        inputElement = (
          <input
            type="date"
            id={fieldId}
            value={value ? value.split('T')[0] : ''}
            onChange={(e) => handleChange(column.name, e.target.value)}
            className="form-input"
            required={isRequired}
          />
        );
        break;
        
      case 'int':
      case 'decimal2':
      case 'decimal3':
      case 'decimal4':
      case 'float':
        inputElement = (
          <input
            type="number"
            id={fieldId}
            value={value}
            onChange={(e) => handleChange(column.name, e.target.value)}
            className="form-input"
            placeholder={''}
            step={colType === 'int' ? '1' : 'any'}
            required={isRequired}
          />
        );
        break;
        
      case 'textselectable':
      case 'selectable':
        // Auto-complete functionality for selectable text fields
        inputElement = (
          <SelectableInput
            id={fieldId}
            value={value}
            onChange={(newValue) => handleChange(column.name, newValue)}
            className="form-input"
            placeholder={''}
            tableName={tableName}
            columnName={column.name}
            required={isRequired}
            options={options}
          />
        );
        break;
        
      case 'text':
      case 'string':
      default:
        // Check if field is configured as lookup (new functionality)
        if (isFieldLookup(column)) {
          inputElement = (
            <LookupField
              field={column}
              value={value}
              onChange={(newValue) => handleChange(column.name, newValue)}
              onMultiFieldChange={handleMultiFieldChange}
              className="form-input"
              placeholder={`Select ${column.displayName || column.label || column.name}...`}
              disabled={false}
            />
          );
        }
        // Check if field has selectable options (existing functionality)
        else if (isFieldSelectable(column)) {
          inputElement = (
            <SelectableInput
              id={fieldId}
              value={value}
              onChange={(newValue) => handleChange(column.name, newValue)}
              className="form-input"
              placeholder={''}
              tableName={tableName}
              columnName={column.name}
              required={isRequired}
            />
          );
        } else {
          // Standard text input
          inputElement = (
            <input
              type="text"
              id={fieldId}
              value={value}
              onChange={(e) => handleChange(column.name, e.target.value)}
              className="form-input"
              placeholder={''}
              required={isRequired}
            />
          );
        }
    }

    return (
      <div 
        key={column.name} 
        className={`form-field ${colType === 'textarea' ? 'full-width' : ''}`}
        style={colType === 'textarea' ? { gridColumn: '1 / -1' } : {}}
      >
        <label htmlFor={fieldId} className="form-label">
          {column.displayName || column.label || column.name}
          {resolvedUnit && <span className="unit-display"> ({resolvedUnit})</span>}
          {isRequired && <span className="required-asterisk"> *</span>}
        </label>
        <div style={{ display: 'flex', alignItems: 'left' }}>
          {inputElement}
          {column.calculationFunction && (
            <button
              type="button"
              className="cal-btn"
              onClick={() => handleCustomCalculate(column)}
              style={{ marginLeft: '8px' }}
              title="Calculate"
            >
              Cal
            </button>
          )}
        </div>
        {errors[column.name] && (
          <div className="field-error">{errors[column.name]}</div>
        )}
      </div>
    );
  };

  // Render table view for editing multiple records
  const renderTableView = () => {
    if (!isTableViewMode || !sectionConfig) return null;

    const enabledFields = sectionConfig.fields.filter(field => field.enabled);

    // Helper function to check if a row has data
    const hasRowData = (row) => {
      return enabledFields.some(field => {
        const value = row[field.dbColumn];
        return value !== null && value !== undefined && value !== '';
      });
    };

    // Helper function to render field input for table cells
    const renderTableFieldInput = (field, value, rowIndex) => {
      const fieldFormat = field.format?.toLowerCase() || 'text';
      const fieldValue = value || '';
      
      const onChange = (e) => handleTableFieldChange(rowIndex, field.dbColumn, e.target.value);

      // Format time value for HTML time input (same logic as DDR_v2)
      const formatTimeValue = (value) => {
        if (!value) return '';
        
        if (typeof value === 'string') {
          if (/^\d{2}:\d{2}$/.test(value)) return value;
          if (value.includes('T')) {
            const timePart = value.split('T')[1];
            if (timePart) return timePart.substring(0, 5);
          }
          if (/^\d{2}:\d{2}:\d{2}/.test(value)) return value.substring(0, 5);
          if (/^\d{1,2}:\d{2}$/.test(value)) {
            const parts = value.split(':');
            return `${parts[0].padStart(2, '0')}:${parts[1]}`;
          }
        }
        return value;
      };

      const inputStyle = {
        width: '100%',
        padding: '6px 8px',
        border: '1px solid #d1d5db',
        borderRadius: '4px',
        fontSize: '13px',
        fontFamily: 'inherit',
        backgroundColor: '#cceda2',
        boxSizing: 'border-box'
      };

      switch (fieldFormat) {
        case 'time':
          const formattedTimeValue = formatTimeValue(fieldValue);
          return (
            <input
              type="time"
              value={formattedTimeValue}
              onChange={onChange}
              style={{ ...inputStyle, minWidth: '100px' }}
            />
          );

        case 'date':
          return (
            <input
              type="date"
              value={fieldValue ? fieldValue.split('T')[0] : ''}
              onChange={onChange}
              style={inputStyle}
            />
          );

        case 'number':
        case 'currency':
          return (
            <input
              type="number"
              value={fieldValue}
              onChange={onChange}
              placeholder={field.placeholder || field.name}
              step={field.numberFormat?.includes('.') ? 'any' : '1'}
              style={{ ...inputStyle, textAlign: 'right' }}
            />
          );

        case 'textarea':
          return (
            <textarea
              value={fieldValue}
              onChange={onChange}
              rows={3}
              placeholder={field.placeholder || field.name}
              style={{ ...inputStyle, minHeight: '50px', resize: 'vertical' }}
            />
          );

        case 'select':
        case 'dropdown':
          // Common options for activity fields
          const getFieldOptions = () => {
            const commonOptions = {
              'Category': ['Drilling', 'Tripping', 'Circulation', 'Logging', 'Casing', 'Cementing', 'Testing', 'Maintenance', 'Waiting'],
              'JobCode': ['DRL', 'TRP', 'CIR', 'LOG', 'CSG', 'CMT', 'TST', 'MNT', 'WOW'],
              'Status': ['Active', 'Complete', 'In Progress', 'On Hold', 'Cancelled']
            };
            return commonOptions[field.dbColumn] || [];
          };

          const options = getFieldOptions();
          return (
            <select
              value={fieldValue}
              onChange={onChange}
              style={{ ...inputStyle, cursor: 'pointer' }}
            >
              <option value="">Select {field.name}...</option>
              {options.map((option, idx) => (
                <option key={idx} value={option}>{option}</option>
              ))}
            </select>
          );

        case 'lookup':
          // Handle lookup fields specifically (from DDR Layout Manager)
          console.log('🔍 DataForm: Rendering lookup field:', {
            fieldName: field.name,
            dbColumn: field.dbColumn,
            format: field.format,
            options: field.options,
            lookupTable: field.lookupTable,
            lookupValueColumn: field.lookupValueColumn,
            lookupDisplayColumn: field.lookupDisplayColumn,
            lookupReturnColumn: field.lookupReturnColumn,
            value: fieldValue
          });
          
          return (
            <LookupField
              field={field}
              value={fieldValue}
              onChange={(newValue) => handleTableFieldChange(rowIndex, field.dbColumn, newValue)}
              onMultiFieldChange={(fieldMappings) => handleTableMultiFieldChange(rowIndex, fieldMappings)}
              placeholder={field.placeholder || `Select ${field.name}...`}
              disabled={false}
              className=""
            />
          );

        case 'text':
        default:
          // Check if field is configured as lookup (fallback check for column config)
          if (field.options === 'lookup' && field.lookupTable && field.lookupValueColumn && field.lookupDisplayColumn) {
            console.log('🔍 DataForm: Rendering lookup field (fallback):', {
              fieldName: field.name,
              dbColumn: field.dbColumn,
              format: field.format,
              options: field.options,
              lookupTable: field.lookupTable,
              lookupValueColumn: field.lookupValueColumn,
              lookupDisplayColumn: field.lookupDisplayColumn,
              lookupReturnColumn: field.lookupReturnColumn,
              value: fieldValue
            });
            
            return (
              <LookupField
                field={field}
                value={fieldValue}
                onChange={(newValue) => handleTableFieldChange(rowIndex, field.dbColumn, newValue)}
                onMultiFieldChange={(fieldMappings) => handleTableMultiFieldChange(rowIndex, fieldMappings)}
                placeholder={field.placeholder || `Select ${field.name}...`}
                disabled={false}
                className=""
              />
            );
          }
          // Standard text input
          return (
            <input
              type="text"
              value={fieldValue}
              onChange={onChange}
              placeholder={field.placeholder || field.name}
              style={inputStyle}
            />
          );
      }
    };

    return (
      <div className="table-view-container">
        <div className="table-view-header">
          <h4>📊 {sectionConfig.name} - Table View</h4>
          <div className="table-legend">
            <div className="legend-item">
              <div className="status-indicator has-data"></div>
              <span>Rows with data</span>
            </div>
            <div className="legend-item">
              <div className="status-indicator empty"></div>
              <span>Empty rows</span>
            </div>
          </div>
        </div>

        <div className="table-view-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ width: '30px' }}>Status</th>
                {enabledFields.map(field => (
                  <th key={field.id} style={{ width: field.width !== 'auto' ? field.width : undefined }}>
                    {field.name}
                  </th>
                ))}
                <th style={{ width: '60px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {tableRows.length > 0 ? (
                tableRows.map((row, rowIndex) => {
                  const rowHasData = hasRowData(row);
                  return (
                    <tr 
                      key={rowIndex}
                      data-has-data={rowHasData.toString()}
                      style={{
                        borderBottom: '1px solid #e5e7eb',
                        backgroundColor: rowHasData ? 'transparent' : '#fffbeb'
                      }}
                    >
                      <td>
                        <div 
                          className={`status-indicator ${rowHasData ? 'has-data' : 'empty'}`}
                          title={rowHasData ? 'Row has data' : 'Empty row'}
                        />
                      </td>
                      {enabledFields.map(field => (
                        <td key={field.id}>
                          {renderTableFieldInput(field, row[field.dbColumn], rowIndex)}
                        </td>
                      ))}
                      <td>
                        <button
                          type="button"
                          className="remove-row-btn"
                          onClick={() => handleRemoveTableRow(rowIndex)}
                          title="Remove row"
                        >
                          🗑️
                        </button>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={enabledFields.length + 2} style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>
                    No data available. Click "Add Row" to add data.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="table-view-footer">
          <button
            type="button"
            className="add-row-btn"
            onClick={handleAddTableRow}
          >
            ➕ Add Row
          </button>
        </div>
      </div>
    );
  };

  // Render parent form for dual table mode
  const renderParentForm = () => {
    if (!isDualTableMode || !parentConfig?.columns) return null;

    return (
      <div className="parent-form-section">
        <h3>{parentConfig.displayName || tables.parent} Details</h3>
        <div 
          className="form-fields" 
          style={{ 
            gridTemplateColumns: `repeat(${columnCount}, 1fr)` 
          }}
        >
          {parentConfig.columns
            .filter(col => 
              col.name !== identityColumns.parent && 
              !hiddenParentFields.includes(col.name)
            )
            .map(column => {
              const value = parentData[column.name] || '';
              const colType = column.type ? column.type.toLowerCase() : 'text';
              const fieldId = `field-${column.name}`;
              const isRequired = column.required || false;
              const resolvedUnit = resolveFieldUnitSync(column, globalSettings);
              const error = errors[column.name];
              
              let inputElement;

              switch (colType) {
                case 'textarea':
                  inputElement = (
                    <textarea
                      id={fieldId}
                      value={value}
                      onChange={(e) => handleParentChange(column.name, e.target.value)}
                      rows={4}
                      className="form-textarea"
                      placeholder={column.desc || ''}
                      required={isRequired}
                    />
                  );
                  break;
                  
                case 'select':
                  const options = column.options || [];
                  inputElement = (
                    <select
                      id={fieldId}
                      value={value}
                      onChange={(e) => handleParentChange(column.name, e.target.value)}
                      className="form-select"
                      required={isRequired}
                    >
                      <option value="">Select...</option>
                      {options.map((option, idx) => (
                        <option key={idx} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  );
                  break;
                  
                case 'date':
                  inputElement = (
                    <input
                      type="date"
                      id={fieldId}
                      value={value ? value.split('T')[0] : ''}
                      onChange={(e) => handleParentChange(column.name, e.target.value)}
                      className="form-input"
                      required={isRequired}
                    />
                  );
                  break;
                  
                case 'int':
                case 'decimal2':
                case 'decimal3':
                case 'decimal4':
                case 'float':
                  inputElement = (
                    <input
                      type="number"
                      id={fieldId}
                      value={value}
                      onChange={(e) => handleParentChange(column.name, e.target.value)}
                      className="form-input"
                      placeholder={column.desc || ''}
                      step={colType === 'int' ? '1' : 'any'}
                      required={isRequired}
                    />
                  );
                  break;
                  
                case 'boolean':
                  inputElement = (
                    <select
                      id={fieldId}
                      value={value || ''}
                      onChange={(e) => handleParentChange(column.name, e.target.value === 'true')}
                      className="form-select"
                      required={isRequired}
                    >
                      <option value="">Select...</option>
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </select>
                  );
                  break;
                  
                case 'text':
                case 'string':
                default:
                  inputElement = (
                    <input
                      type="text"
                      id={fieldId}
                      value={value}
                      onChange={(e) => handleParentChange(column.name, e.target.value)}
                      className="form-input"
                      placeholder={column.desc || ''}
                      required={isRequired}
                    />
                  );
              }

              return (
                <div 
                  key={column.name} 
                  className={`form-field ${colType === 'textarea' ? 'full-width' : ''}`}
                  data-type={colType}
                >
                  <label htmlFor={fieldId} className="form-label">
                    {column.desc && (
                      <span className="label-tooltip" title={column.desc}>
                        ℹ️
                      </span>
                    )}
                    <span className="label-text">
                      {column.label || column.name}
                      {isRequired && <span className="required-indicator">*</span>}
                      {resolvedUnit && <span className="unit-label">({resolvedUnit})</span>}
                    </span>
                  </label>
                  {inputElement}
                  {error && <div className="field-error">{error}</div>}
                </div>
              );
            })}
        </div>
      </div>
    );
  };

  // Column resizing functionality for dual table mode
  const handleMouseDown = useCallback((e, columnIndex) => {
    e.preventDefault();
    const table = e.target.closest('table');
    const th = table.querySelectorAll('th')[columnIndex];
    const startX = e.clientX;
    const startWidth = th.offsetWidth;

    setIsResizing(true);
    setResizeData({ columnIndex, startX, startWidth, table });
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (!isResizing || !resizeData) return;

    const { columnIndex, startX, startWidth, table } = resizeData;
    const diff = e.clientX - startX;
    const newWidth = Math.max(100, startWidth + diff);

    const th = table.querySelectorAll('th')[columnIndex];
    const tds = table.querySelectorAll(`td:nth-child(${columnIndex + 1})`);
    
    th.style.width = `${newWidth}px`;
    th.style.minWidth = `${newWidth}px`;
    th.style.maxWidth = `${newWidth}px`;
    
    tds.forEach(td => {
      td.style.width = `${newWidth}px`;
      td.style.minWidth = `${newWidth}px`;
      td.style.maxWidth = `${newWidth}px`;
    });
  }, [isResizing, resizeData]);

  const handleMouseUp = useCallback(() => {
    setIsResizing(false);
    setResizeData(null);
  }, []);

  // Add global mouse event listeners for resizing
  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    } else {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing, handleMouseMove, handleMouseUp]);

  // Render child form as table for dual table mode
  const renderChildForm = () => {
    if (!isDualTableMode || !childConfig?.columns) return null;

    const childColumns = childConfig.columns.filter(col => 
      col.name !== identityColumns.child && 
      col.name !== matchKey.childForeignKey
    );

    return (
      <div className="child-form-section">
        <h3>{childConfig.displayName || tables.child} Records</h3>
        
        <div className="child-table-container">
          <table className="child-table">
            <thead>
              <tr>
                {childColumns.map((column, index) => (
                  <th key={column.name} className="resizable-column">
                    <div className="column-header">
                      <span>{column.label || column.name}</span>
                      {column.required && <span className="required">*</span>}
                    </div>
                    <div 
                      className="column-resizer"
                      onMouseDown={(e) => handleMouseDown(e, index)}
                    ></div>
                  </th>
                ))}
                <th className="actions-column">Actions</th>
              </tr>
            </thead>
            <tbody>
              {/* Existing child records */}
              {childData.map((child, index) => (
                <tr key={index} className="child-row">
                  {childColumns.map(column => {
                    const error = errors[`child_${index}_${column.name}`];
                    return (
                      <td key={column.name} className={error ? 'error-cell' : ''}>
                        {renderTableField(
                          column, 
                          child[column.name], 
                          (fieldName, value) => handleChildChange(index, fieldName, value),
                          `child_${index}_${column.name}`
                        )}
                        {error && <div className="table-field-error">{error}</div>}
                      </td>
                    );
                  })}
                  <td className="actions-cell">
                    <button
                      type="button"
                      onClick={() => handleRemoveChild(index)}
                      className="remove-row-btn"
                      title="Remove this record"
                    >
                      ✕
                    </button>
                  </td>
                </tr>
              ))}
              
              {/* Add new child record row */}
              <tr className="add-row" style={{ backgroundColor: '#fffbeb' }}>
                {childColumns.map(column => {
                  const error = errors[`new_${column.name}`];
                  return (
                    <td key={column.name} className={error ? 'error-cell' : ''}>
                      {renderTableField(
                        column, 
                        newChildRecord[column.name], 
                        (fieldName, value) => {
                          console.log(`🎯 Table field change for NEW record: ${fieldName} = "${value}"`);
                          handleNewChildChange(fieldName, value);
                        },
                        `new_${column.name}`
                      )}
                      {error && <div className="table-field-error">{error}</div>}
                    </td>
                  );
                })}
                <td className="actions-cell">
                  <button
                    type="button"
                    onClick={() => {
                      console.log('🖱️ Add Row button clicked');
                      console.log('newChildRecord keys:', Object.keys(newChildRecord));
                      console.log('newChildRecord data:', newChildRecord);
                      handleAddChild();
                    }}
                    className="add-row-btn"
                    disabled={!Object.keys(newChildRecord).length}
                    title="Add this record"
                  >
                    ➕
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // Render table field for dual table mode (parent-child relationships)
  const renderTableField = (column, value, onChange, fieldId) => {
    const colType = column.type ? column.type.toLowerCase() : 'text';
    const fieldValue = value || '';
    
    const handleChange = (e) => {
      onChange(column.name, e.target.value);
    };

    // Resolve unit (handle "global" units)
    const resolvedUnit = resolveFieldUnitSync(column, globalSettings);
    
    const inputStyle = {
      width: '100%',
      padding: '6px 8px',
      border: '1px solid #d1d5db',
      borderRadius: '4px',
      fontSize: '13px',
      fontFamily: 'inherit',
      backgroundColor: '#cceda2',
      boxSizing: 'border-box'
    };

    switch (colType) {
      case 'textarea':
        return (
          <textarea
            id={fieldId}
            value={fieldValue}
            onChange={handleChange}
            rows={3}
            className="table-field-input"
            placeholder={column.desc || column.name}
            style={{ ...inputStyle, minHeight: '50px', resize: 'vertical' }}
          />
        );
        
      case 'select':
        const options = column.options || [];
        return (
          <select
            id={fieldId}
            value={fieldValue}
            onChange={handleChange}
            className="table-field-input"
            style={{ ...inputStyle, cursor: 'pointer' }}
          >
            <option value="">Select...</option>
            {options.map((option, idx) => (
              <option key={idx} value={option}>
                {option}
              </option>
            ))}
          </select>
        );
        
      case 'date':
        return (
          <input
            type="date"
            id={fieldId}
            value={fieldValue ? fieldValue.split('T')[0] : ''}
            onChange={handleChange}
            className="table-field-input"
            style={inputStyle}
          />
        );
        
      case 'int':
      case 'decimal2':
      case 'decimal3':
      case 'decimal4':
      case 'float':
        return (
          <input
            type="number"
            id={fieldId}
            value={fieldValue}
            onChange={handleChange}
            className="table-field-input"
            placeholder={column.desc || column.name}
            step={colType === 'int' ? '1' : 'any'}
            style={{ ...inputStyle, textAlign: 'right' }}
          />
        );
        
      case 'textselectable':
      case 'selectable':
        // Auto-complete functionality for selectable text fields
        return (
          <SelectableInput
            id={fieldId}
            value={fieldValue}
            onChange={(newValue) => onChange(column.name, newValue)}
            className="table-field-input"
            placeholder={column.desc || column.name}
            tableName={tables.child}
            columnName={column.name}
            style={inputStyle}
          />
        );
        
      case 'text':
      case 'string':
      default:
        // Check if field has selectable options (new format)
        if (isFieldSelectable(column)) {
          return (
            <SelectableInput
              id={fieldId}
              value={fieldValue}
              onChange={(newValue) => onChange(column.name, newValue)}
              className="table-field-input"
              placeholder={column.desc || column.name}
              tableName={tables.child}
              columnName={column.name}
              style={inputStyle}
            />
          );
        } else {
          // Standard text input
          return (
            <input
              type="text"
              id={fieldId}
              value={fieldValue}
              onChange={handleChange}
              className="table-field-input"
              placeholder={column.desc || column.name}
              style={inputStyle}
            />
          );
        }
    }
  };

  if (isLoading) {
    return (
      <div className="data-form-overlay">
        <div className="data-form">
          <div className="form-loading">
            <div className="loading-spinner"></div>
            <p>Loading form configuration...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="data-form-overlay">
        <div className="data-form">
          <div className="form-header">
            <h3>Error</h3>
            <button className="btn-close" onClick={handleCancel}>×</button>
          </div>
          <div className="form-error">
            <p>{error}</p>
            <button className="btn btn-secondary" onClick={handleCancel}>
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Determine if editing or new record for title with key column info
  let recordMode = '';
  let keyColumnInfo = '';
  
  // Helper function to get column display name
  const getColumnDisplayName = (columnName) => {
    // Check table config first (single table mode)
    if (tableConfig?.columns) {
      const col = tableConfig.columns.find(c => c.name === columnName);
      if (col) return col.label || col.displayName || col.name;
    }
    
    // Check parent config (dual table mode)
    if (parentConfig?.columns) {
      const col = parentConfig.columns.find(c => c.name === columnName);
      if (col) return col.label || col.displayName || col.name;
    }
    
    // Check child config (dual table mode)
    if (childConfig?.columns) {
      const col = childConfig.columns.find(c => c.name === columnName);
      if (col) return col.label || col.displayName || col.name;
    }
    
    // Fallback to column name
    return columnName;
  };

  // Build key column info for title
  if (reservedKeyColumns && Object.keys(reservedKeyColumns).length > 0) {
    const keyColumnEntries = Object.entries(reservedKeyColumns)
      .filter(([_, value]) => value !== null && value !== undefined && value !== '')
      .map(([columnName, value]) => {
        const displayName = getColumnDisplayName(columnName);
        return `${displayName}=${value}`;
      });
    
    if (keyColumnEntries.length > 0) {
      keyColumnInfo = ` (${keyColumnEntries.join(', ')})`;
    }
  }

  if (isSingleTableMode) {
    // If any field in formData has a non-empty value, consider as edit
    const hasData = Object.values(formData).some(
      v => v !== undefined && v !== null && String(v).trim() !== ''
    );
    recordMode = hasData ? 'Editing' : 'New';
  } else if (isDualTableMode) {
    // If any field in parentData has a non-empty value, consider as edit
    const hasParentData = parentData && Object.values(parentData).some(
      v => v !== undefined && v !== null && String(v).trim() !== ''
    );
    recordMode = hasParentData ? 'Editing' : 'New';
  }
  
  const displayTitle = `${recordMode} ${title}${keyColumnInfo}`;

  return (
    <div className="data-form-overlay">
      <div 
        ref={dataFormRef}
        className="data-form"
        style={{ '--column-multiplier': Math.max(1, columnCount - 1) }}
      >
        <div className="form-header">
          <h3>{displayTitle}</h3>
          <div className="form-controls">
            <div className="header-left-controls">
              <button
                type="button"
                className="file-import-hint"
                onClick={() => {
                  if (dropZoneRef.current && dropZoneRef.current.querySelector('input[type="file"]')) {
                    dropZoneRef.current.querySelector('input[type="file"]').click();
                  }
                }}
                title="Import data from file (Excel, CSV, PDF, etc.)"
              >
                📁 Import File
              </button>
              <div className="column-controls">
                <label className="column-control-label">Columns:</label>
                <button 
                  type="button" 
                  className="column-btn" 
                  onClick={() => setColumnCount(Math.max(1, columnCount - 1))}
                  disabled={columnCount <= 1}
                  title="Decrease columns"
                >
                  −
                </button>
                <span className="column-count">{columnCount}</span>
                <button 
                  type="button" 
                  className="column-btn" 
                  onClick={() => setColumnCount(Math.min(4, columnCount + 1))}
                  disabled={columnCount >= 4}
                  title="Increase columns"
                >
                  +
                </button>
              </div>
            </div>
            <div className="header-actions">
              <button 
                type="button" 
                className="btn btn-secondary" 
                onClick={handleCancel}
                disabled={isSaving}
              >
                Cancel
              </button>
              <button 
                type="submit" 
                className="btn btn-primary" 
                disabled={isSaving}
                form="data-form"
              >
                {isSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
          <button className="btn-close" onClick={handleCancel}>×</button>
        </div>
        
        <form id="data-form" onSubmit={handleSubmit} className="form-content">
          {/* Error Banner */}
          {(error || errors.general) && (
            <div className="form-error-banner" style={{
              background: '#fee2e2',
              border: '1px solid #fecaca',
              borderRadius: '6px',
              padding: '12px',
              marginBottom: '16px',
              color: '#dc2626'
            }}>
              <strong>⚠️ Save Error:</strong> {error || errors.general}
            </div>
          )}
          
          <FileDropZone
            ref={dropZoneRef}
            onFileDrop={handleFileDrop}
            className="form-drop-zone"
            acceptedTypes={['.xlsx', '.xls', '.csv', '.txt', '.pdf', '.doc', '.docx', '.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp']}
          >
            {isTableViewMode ? (
              // Table view mode - render table for editing multiple records
              renderTableView()
            ) : isSingleTableMode ? (
              // Single table mode - render form fields
              <div 
                className="form-fields" 
                style={{ 
                  gridTemplateColumns: `repeat(${columnCount}, 1fr)` 
                }}
              >
                {columns.map(column => renderField(column))}
              </div>
            ) : isDualTableMode ? (
              // Dual table mode - render parent form and child table
              <div className="dataform2-sections">
                {renderParentForm()}
                {renderChildForm()}
              </div>
            ) : null}
          </FileDropZone>

          {/* Data Extraction Component */}
          {droppedFile && (
            <DataExtraction
              // Form configuration
              isSingleTableMode={isSingleTableMode}
              isDualTableMode={isDualTableMode}
              isTableViewMode={isTableViewMode}
              tableName={tableName}
              tables={tables}
              columns={columns}
              tableConfig={tableConfig}
              parentConfig={parentConfig}
              childConfig={childConfig}
              sectionConfig={sectionConfig}
              
              // Data and handlers
              formData={formData}
              parentData={parentData}
              matchKey={matchKey}
              
              // Callbacks
              onDataExtracted={handleDataExtracted}
              onAttachmentSaved={handleAttachmentSaved}
              onDebugInfoChanged={handleDebugInfoChanged}
              
              // UI state
              droppedFile={droppedFile}
              onFileProcessed={handleFileProcessed}
            />
          )}

          {/* Debug Button - Shows after AI extraction completion */}
          {debugInfo && !showDebugPanel && (
            <div className="debug-button-container">
              <button 
                className="debug-review-button"
                onClick={() => setShowDebugPanel(true)}
                title="Review AI extraction debug information"
              >
                🔍 Debug Review
              </button>
              <div className="debug-button-info">
                <span>Last extraction: {debugInfo.method}</span>
                <span>Confidence: {debugInfo.confidence ? (debugInfo.confidence * 100).toFixed(1) + '%' : 'N/A'}</span>
                <span>File: {debugInfo.fileName}</span>
              </div>
            </div>
          )}
        </form>

        {/* Key Column Conflicts Panel */}
        {keyColumnConflicts.length > 0 && (
          <KeyColumnConflictsPanel 
            conflicts={keyColumnConflicts} 
            onClose={clearKeyColumnConflicts} 
          />
        )}

        {/* Debug Panel */}
        {showDebugPanel && (
          <DebugPanel 
            debugInfo={debugInfo} 
            onClose={() => setShowDebugPanel(false)} 
          />
        )}
      </div>
    </div>
  );
} 